import PMBSelect from 'components/PMBSelect'
import { useUploadImagePublicMutation } from 'features/private-views/settings/settingApiSlice'
import useAutoFetchPublicVersion from 'hooks/useAutoFetchPublicVersion'
import useIsMobile from 'hooks/useIsMobile'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap'
import {
  useCreateMasterItemMutation,
  useUpdateMasterItemMutation,
} from './masterItemsApiSlice'

const ModalAddEditMasterItem = ({
  isOpen,
  toggle,
  dataItemById = null,
  onSuccess,
  parentItems = [],
}) => {
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const inputRef = useRef(null)

  const isMobile = useIsMobile()

  const [createMasterItem, { isLoading: isCreating }] =
    useCreateMasterItemMutation()
  const [updateMasterItem, { isLoading: isUpdating }] =
    useUpdateMasterItemMutation()

  const isEditMode = Boolean(dataItemById)

  const [payload, setPayload] = useState({
    nameItem: '',
    grade: 1,
    linkFoto: '',
    unit: null,
    parentId: null,
    vat: null,
    desc: '',
    SKU: null,
  })

  const [imagePreview, setImagePreview] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  // HANDLE EDIT AND SET VALUE
  useEffect(() => {
    if (isEditMode && dataItemById) {
      let description = dataItemById?.attribute || ''
      try {
        const parsed = JSON.parse(description)
        if (parsed && typeof parsed === 'object' && parsed.desc) {
          description = parsed.desc
        }
      } catch (e) {
        // Ignore error
      }

      setPayload({
        id: dataItemById?.id || null,
        nameItem: dataItemById?.nameItem || '',
        grade: dataItemById?.grade || 1,
        linkFoto: dataItemById?.linkFoto || '',
        parentId: dataItemById?.parentId || null,
        unit: dataItemById?.unit
          ? { label: dataItemById?.unit, value: dataItemById?.unit }
          : null,
        vat: null,
        desc: description,
        SKU: dataItemById?.SKU || null,
      })
      setImagePreview(dataItemById?.linkFoto || null)
    } else {
      setPayload({
        nameItem: '',
        grade: 1,
        linkFoto: '',
        unit: null,
        parentId: null,
        vat: null,
        attribute: '',
        SKU: null,
      })
      setImagePreview(null)
    }
  }, [isEditMode, dataItemById, isOpen])

  const autoFetchPublicVersion = useAutoFetchPublicVersion()

  // HANDLE CHANGE VALUE
  const handleChange = (e) => {
    const { name, value } = e.target

    if (value !== payload[name]) {
      setPayload((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  // HANDLE IMAGE UPLOAD
  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result)
      }
      reader.readAsDataURL(file)
      setImageFile(file)
      setPayload((prev) => ({
        ...prev,
        linkFoto: '',
      }))
    }
  }

  // FORM VALIDATE
  const isFormInvalid =
    !payload.nameItem ||
    !payload.grade ||
    (!payload.linkFoto && !imageFile) ||
    !payload.unit

  // HANDLE SUBMIT
  const [uploadImagePublic] = useUploadImagePublicMutation()

  const handleSubmit = async () => {
    try {
      let finalPayload = { ...payload, parentId: null }
      finalPayload.unit =
        payload?.unit?.value ?? payload?.unit?.label ?? payload?.unit ?? null
      if (imageFile) {
        try {
          setIsUploading(true)
          const uploadResult = await uploadImagePublic(imageFile).unwrap()
          setIsUploading(false)
          if (uploadResult?.url) {
            finalPayload.linkFoto = uploadResult.url
          } else if (uploadResult?.error) {
            enqueueSnackbar(uploadResult?.payload || 'Upload gagal', {
              variant: 'error',
            })
            return
          }
        } catch (uploadErr) {
          setIsUploading(false)
          console.error('Upload error:', uploadErr)
          enqueueSnackbar(
            uploadErr?.data?.meta?.message ||
              uploadErr?.message ||
              'Upload gagal',
            { variant: 'error' },
          )
          return
        }
      }

      const response = isEditMode
        ? await updateMasterItem({
            id: dataItemById?.id,
            nameItem: payload?.nameItem,
            desc: payload?.desc,
            unit: finalPayload?.unit,
            linkFoto: payload?.linkFoto,
          }).unwrap()
        : await createMasterItem(finalPayload).unwrap()

      if (response && !response?.error) {
        enqueueSnackbar(
          isEditMode
            ? 'Item berhasil diperbarui!'
            : 'Item berhasil ditambahkan!',
          { variant: 'success' },
        )
        setImageFile(null)
        onSuccess?.()
        toggle()
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      enqueueSnackbar(
        error?.data?.meta?.message ||
          error?.data?.message ||
          error?.message ||
          'Terjadi kesalahan!',
        {
          variant: 'error',
        },
      )
    }
  }

  // AUTO FOCUS
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered={!isMobile}
      fullscreen={isMobile ? true : undefined}
      size={isMobile ? undefined : 'lg'}
      style={!isMobile ? { maxWidth: '700px' } : undefined}
      data-testid="master-item-modal-add-edit"
    >
      <ModalHeader
        toggle={toggle}
        className={isMobile ? 'sticky-top bg-white' : ''}
        style={isMobile ? { zIndex: 1000 } : {}}
      >
        <span className={isMobile ? 'fs-6' : ''}>
          {isEditMode ? 'Edit Barang' : 'Tambah Barang'}
        </span>
      </ModalHeader>
      <ModalBody
        style={!isMobile ? { maxHeight: '70vh', overflowY: 'auto' } : {}}
      >
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for="nameItem">
                Nama Barang <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <Input
                id="nameItem"
                name="nameItem"
                placeholder="Masukkan nama barang"
                value={payload.nameItem}
                onChange={handleChange}
                innerRef={inputRef}
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="desc">Deskripsi</Label>
              <Input
                id="desc"
                name="desc"
                type="textarea"
                rows={3}
                placeholder="Masukkan deskripsi barang"
                value={payload.desc}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="unit">
                Satuan <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <PMBSelect
                inputId="unit"
                placeholder="Pilih unit"
                options={autoFetchPublicVersion?.unitArray ?? []}
                value={payload.unit}
                onChange={(opt) =>
                  setPayload((prev) => ({
                    ...prev,
                    unit: opt,
                  }))
                }
                isClearable
              />
            </FormGroup>
          </Col>

          <Col md={12}>
            <FormGroup>
              <Label for="linkFoto">
                Link Foto / Upload Gambar{' '}
                <span style={{ color: '#b42218' }}>*</span>
              </Label>
              <Input
                id="linkFoto"
                name="linkFoto"
                type="text"
                placeholder="Masukkan URL foto atau upload file"
                value={payload.linkFoto}
                onChange={handleChange}
                className="mb-2"
              />
              <Input
                id="linkFotoFile"
                name="linkFotoFile"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                    }}
                  />
                </div>
              )}
            </FormGroup>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <div className="d-flex gap-3 w-100">
          <Button
            onClick={toggle}
            className="btn-cancel"
            outline
            block
            data-testid="master-item-modal-add-edit-cancel-button"
          >
            Batal
          </Button>
          <Button
            color="primary"
            block
            disabled={isCreating || isUploading || isFormInvalid}
            onClick={handleSubmit}
            data-testid="master-item-modal-add-edit-submit-button"
          >
            {(isCreating || isUploading) && (
              <Spinner size="sm" className="me-2" />
            )}
            {isEditMode ? 'Simpan' : 'Tambah Item'}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default ModalAddEditMasterItem
